/* eslint-disable no-unreachable */
/* eslint-disable react/no-danger */
import { useContext, useEffect, useState } from 'react';

import { Container, ErrorState, LoadingOlist } from 'components/layout';
import ButtonSwitcher from 'components/layout/button-switcher/button-switcher';
import PlansChoiceSelector from 'components/plans-choice/plans-choice-selector/plans-choice-selector';
import { AuthContext } from 'contexts/auth';
import { sendGTMEvent } from 'helpers/google-tag-manager';
import { DEFAULT_LANGUAGE } from 'helpers/i18n-next';
import useMixPanel from 'hooks/use-mixpanel';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import Api from 'service';
import { formatCurrency } from 'utils/old-ui-commons/formats';

import { Flex, Text } from '@olist/united';

import { Faqs } from './faqs/faqs';
import { ModalRules } from './modal-rules/modal-rules';
import { PlansContainer } from './plan-choice.style';
import { Resources } from './resources/resources';

const PlanChoice = () => {
  const { t } = useTranslation();

  const { isGtmLoaded, userInfo } = useContext(AuthContext);

  const { track } = useMixPanel();
  const [selectedPeriodicity, setSelectedPeriodicity] = useState(12);
  const [isOpenModal, setIsOpenModal] = useState(false);

  useEffect(() => {
    track('Plans Choice - Viewed Plans Choice Page');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userInfo?.email && isGtmLoaded) {
      sendGTMEvent('page_view', { user_id: userInfo.email });
    }
  }, [userInfo?.email, isGtmLoaded]);

  const handlePeriodicity = (periodicity) => {
    setSelectedPeriodicity(periodicity);
  };

  const [{ error: plansErrors, loading: isLoadingPlans, value: plansResponse }, fetchPlans] =
    useAsyncFn(async () => Api.getPlanListAndRules());

  const [{ loading: isLoadingPlanOptions, value: planOptionsResponse }, fetchPlanOptions] =
    useAsyncFn(async () => Api.getPlanOptions());

  useEffect(() => {
    fetchPlans();
    fetchPlanOptions();
  }, [fetchPlanOptions, fetchPlans]);

  const handleOpenModal = () => {
    setIsOpenModal(true);
  };
  const handleCloseModal = () => {
    setIsOpenModal(false);
  };

  if (plansErrors) {
    return (
      <Flex height="100vh" justifyContent="center" alignItems="center">
        <ErrorState onClick={() => window.location.reload()} />
      </Flex>
    );
  }

  if (isLoadingPlans || !plansResponse || isLoadingPlanOptions) {
    return <LoadingOlist data-testid="qualification-loading-olist" />;
  }

  const formattedPlans = plansResponse?.plansFormatted;

  return (
    <>
      {planOptionsResponse && (
        <ModalRules
          onClose={handleCloseModal}
          isOpen={isOpenModal}
          modalCostTitle={planOptionsResponse.modal_cost_title}
          modalCosts={planOptionsResponse.modal_costs}
          modalExplanations={planOptionsResponse.modal_explanations}
          modalLink={planOptionsResponse.modal_link}
          modalTitle={planOptionsResponse.modal_title}
        />
      )}
      <Container mb={8}>
        <Flex my={3} width={1} flexDirection="column" alignItems="center">
          <Flex
            pb={8}
            flexDirection={['column', 'row']}
            justifyContent={['flex-start', 'space-between']}
            alignItems={['flex-start', 'center']}
            width={1}
          >
            <Text margin="0 auto" fontWeight="bold" fontSize={['15px', 3, 4, 5]} textAlign="center">
              {t`choose the ideal plan for your business`}
            </Text>
          </Flex>
          <ButtonSwitcher
            options={[
              {
                label: t`monthly`,
                onClick: () => handlePeriodicity(1),
                maxDiscount: plansResponse?.maxDiscount[1],
              },
              {
                label: t`yearly`,
                onClick: () => handlePeriodicity(12),
                maxDiscount: plansResponse?.maxDiscount[12],
              },
            ]}
            initialBtn={t`yearly`}
          />
        </Flex>
        {formattedPlans && (
          <PlansContainer>
            {formattedPlans[selectedPeriodicity].map((plan) => {
              const shouldShowDescription = plan.amount !== 0 && plan.period !== 1;
              return (
                <PlansChoiceSelector
                  key={plan.planId}
                  planId={plan.planId}
                  planName={plan.description.title}
                  description={plan.description.description}
                  selfChoose={plan.description.selfChoose}
                  commission={plan.commission}
                  recurringPayment={plan.amountPerMonth}
                  discount={plan?.discount?.percentage || null}
                  periodicity={selectedPeriodicity}
                  hasPersonalData={plansResponse?.hasPersonalData}
                  signupCompleted={plansResponse?.signupCompleted}
                  planBillingId={plan.planBillingId}
                  planSlug={plan.slug}
                  onOpenModal={handleOpenModal}
                  amountDescription={
                    shouldShowDescription
                      ? t`${formatCurrency(plan.amount, false, DEFAULT_LANGUAGE)} in up to ${
                          plan.period
                        }x`
                      : null
                  }
                />
              );
            })}
          </PlansContainer>
        )}

        {planOptionsResponse && (
          <Resources
            resources={planOptionsResponse.resources}
            resourcesDescription={planOptionsResponse.resources_description}
            resourcesTitle={planOptionsResponse.resources_title}
          />
        )}
        {planOptionsResponse && (
          <Faqs faqTitle={planOptionsResponse.faq_title} faqs={planOptionsResponse.faqs} />
        )}
      </Container>
    </>
  );
};

export default PlanChoice;
